import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Type } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { from, Observable, shareReplay, switchMap, tap } from 'rxjs';

import { MAIN_CONTENT_ROUTE_DATA_KEY } from '@ppg/core/content';
import { MixedContentPageType } from '@ppg/core/enums';
import { LoggerService } from '@ppg/core/logger';
import { Content } from '@ppg/core/models';
import { createPendingTaskWatcher } from '@ppg/core/services';

@Component({
  selector: 'ppg-mixed-content-page-resolver',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './mixed-content-page-resolver.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MixedContentPageResolverComponent {
  readonly #logger = inject(LoggerService);
  readonly #activatedRoute = inject(ActivatedRoute);

  readonly #routeData = toSignal(this.#activatedRoute.data, { initialValue: this.#activatedRoute.snapshot.data });
  readonly #mainContent = computed<Content | null>(() => this.#routeData()[MAIN_CONTENT_ROUTE_DATA_KEY] ?? null);

  readonly componentInputs = computed(() => {
    return { content: this.#mainContent() };
  });

  readonly page = computed(() => {
    return this.#mainContent()?.page ?? null;
  });

  readonly #pageRendererComponentLoadTask = createPendingTaskWatcher();
  readonly pageRendererComponent$ = toObservable(this.page).pipe(
    tap(() => this.#pageRendererComponentLoadTask.startTask()),
    switchMap(() => this.#getPageRenderer()),
    tap(() => this.#pageRendererComponentLoadTask.stopTask()),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  #getPageRenderer(): Observable<Type<unknown>> {
    const contentType = this.page()?.type;

    this.#logger.info(`[MixedContentPageResolverComponent] rendering ${contentType}`);

    switch (contentType) {
      case MixedContentPageType.COLOR_DETAILS: {
        return from(import('@ppg/features/color-details').then((m) => m.ColorDetailsComponent));
      }
      case MixedContentPageType.COLOR_FAMILIES: {
        return from(import('@ppg/features/color-families').then((m) => m.ColorFamiliesComponent));
      }
      case MixedContentPageType.PRODUCT_DETAILS: {
        return from(import('@ppg/features/product-details').then((m) => m.ProductDetailsComponent));
      }
      case MixedContentPageType.PRODUCT_LIST: {
        return from(import('@ppg/features/product-list').then((m) => m.ProductListPageComponent));
      }
      case MixedContentPageType.COLORS: {
        return from(import('@ppg/features/color-list').then((m) => m.ColorListPageComponent));
      }
      case MixedContentPageType.COLOR_COLLECTION: {
        return from(import('@ppg/features/color-collection').then((m) => m.ColorCollectionComponent));
      }
      case MixedContentPageType.FAVORITE_COLORS: {
        return from(import('@ppg/features/favorite-colors').then((m) => m.FavoriteColorsComponent));
      }
      default: {
        this.#logger.error(
          `[MixedContentPageResolverComponent] cannot resolve contentType: ${contentType}, rendering 404 page`,
        );
        return from(import('@ppg/features/not-found').then((component) => component.NotfoundComponent));
      }
    }
  }
}
